import { isFaceDownCard } from 'components/Card';

import { CardRef, HandHistoryEvent } from './types';

/**
 * The play-by-play grouped by betting round, oldest first.
 *
 * The 24 Sep demo room could not tell the streets apart when every row looked
 * the same (Victor), and Cory asked for the log to read top-down like the hand
 * was played; Jason agreed on 28 Sep. So the rows are chronological and each
 * betting round is its own container: the hand's setup rows (game number,
 * dealer button, blinds) open the pre-flop round, the "Dealing Flop / Turn /
 * River" rows become the headers of the next rounds and carry the board as
 * dealt so far, and whatever follows the last street — showdown, payouts —
 * stays in that round. On a multi-run hand each runout is a round of its own,
 * headed "Run n" with that run's board, holding that run's payouts.
 */
export type BettingRoundKey = 'preflop' | 'flop' | 'turn' | 'river' | 'run';

export interface RoundBoard {
  cards: CardRef[];
}

export interface BettingRound {
  key: BettingRoundKey;
  /** What the container header says: Pre-flop, Flop, Turn, River — or Run n. */
  label: string;
  /** The runout a "Run n" round deals, 1-based; absent on a street. */
  run?: number;
  /**
   * The viewer's hole cards when known, on every round: the header reads
   * "your cards, then the board" like the card header does, so the pre-flop
   * cards are never mistaken for a board (Jason, 28 Sep).
   */
  holeCards: CardRef[];
  /**
   * The board as dealt so far — one entry for a street or a runout, none
   * before the flop. The header shows the hole cards, then the board.
   */
  boards: RoundBoard[];
  /** The street row that opened the round; the pre-flop round has none. */
  street?: HandHistoryEvent;
  events: HandHistoryEvent[];
}

export interface GroupOptions {
  /**
   * Every board of a run-out on the felt (the live card's `boards`), for a
   * run-out no run has paid yet — so no "Run n" row is in the log to say so.
   */
  runBoards?: ReadonlyArray<ReadonlyArray<CardRef | null | undefined>>;
}

const STREET_TEXT = /dealing (?:the )?(flop|turn|river)/i;
/**
 * A runout's board row on a multi-run hand. The live table log tags it
 * `Run n` (cash-game-broadcast's getTableHistory: one per run, each followed
 * by that run's `Payout n — …` rows); the playground fixtures spell it
 * `Dealing run n`. Anchored, so a row that only mentions a run — a payout,
 * "wins run 1" — is never read as a board.
 */
const RUN_TEXT = /^\s*(?:dealing )?run (\d+)\s*$/i;
/**
 * The processors' own board trail for a run, "Run 2 — AS KD QH 3C 10S"
 * (runMulti.boardTrail), which completed hands published before the capture
 * typed it as a `Run n` street still carry as plain text. The board is read
 * from the wording when the row has no cards attached.
 */
const RUN_TRAIL_TEXT = /^\s*run (\d+)\s+[—–-]\s+((?:(?:10|[2-9AJQK])[CDHS]\s*)+)$/i;

const runNumber = (text?: string) =>
  RUN_TEXT.exec(text || '')?.[1] ?? RUN_TRAIL_TEXT.exec(text || '')?.[1];

const runTrailCards = (text?: string): CardRef[] =>
  RUN_TRAIL_TEXT.exec(text || '')?.[2]
    ?.trim()
    .split(/\s+/) ?? [];

/** True for the wording of a row that deals cards: a street, or a runout's board. */
export const isStreetText = (text?: string) =>
  STREET_TEXT.test(text || '') || runNumber(text) !== undefined;
const LABELS: Record<BettingRoundKey, string> = {
  preflop: 'Pre-flop',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
  run: 'Run'
};
const NEXT: Record<BettingRoundKey, BettingRoundKey> = {
  preflop: 'flop',
  flop: 'turn',
  turn: 'river',
  river: 'river',
  run: 'river'
};

/**
 * The cards actually dealt. The live table log puts every board slot on a
 * street row (`card1`..`card5`, undealt ones empty or face-down), the capture
 * only the dealt ones; both end up as the same strip.
 */
export const dealtCards = (cards?: ReadonlyArray<CardRef | null | undefined>): CardRef[] =>
  (cards ?? []).filter(
    (card): card is CardRef =>
      typeof card === 'string' && card.trim() !== '' && !isFaceDownCard(card)
  );

/** A street row opens a betting round, whether or not the log managed to attach the board to it. */
export const isStreetEvent = (event: HandHistoryEvent) =>
  (event.type === 'street' &&
    (isStreetText(event.text) || dealtCards(event.tableCards).length > 0)) ||
  runTrailCards(event.text).length > 0;

const boardCards = (event: HandHistoryEvent) => {
  const dealt = dealtCards(event.tableCards);
  return dealt.length > 0 ? dealt : runTrailCards(event.text);
};

const streetKey = (event: HandHistoryEvent, previous: BettingRoundKey): BettingRoundKey => {
  const named = STREET_TEXT.exec(event.text || '')?.[1]?.toLowerCase();
  if (named === 'flop' || named === 'turn' || named === 'river') return named;
  const count = dealtCards(event.tableCards).length;
  if (count === 3) return 'flop';
  if (count === 4) return 'turn';
  if (count >= 5) return 'river';
  return NEXT[previous];
};

const STREET_CARDS: Record<string, number> = { flop: 3, turn: 4, river: 5 };

/** How many leading cards every board shares — the streets dealt before the run-out. */
const sharedPrefixLength = (boards: CardRef[][]) => {
  let shared = 0;
  while (
    shared < 5 &&
    boards.every((board) => board[shared] !== undefined && board[shared] === boards[0][shared])
  ) {
    shared++;
  }
  return shared;
};

/**
 * Lay a run-out out run by run.
 *
 * The cards every board shares were dealt before the run-out and stay above
 * the runs, as the betting rounds they are, with their actions — exactly as a
 * single-run hand's (EN-11110 B4/B5). Every street past them belongs to the
 * runs: the log's own "Dealing …" rows for those streets carry run 1's board
 * (the card columns hold run 1 while the boards are dealt), so anything logged
 * under them moves into Run 1. A run has no street rows of its own: its whole
 * board is its header, and its block holds its payouts (B4 — this reverses the
 * per-run Flop / Turn / River rows).
 */
const layOutRuns = (
  rounds: BettingRound[],
  hole: CardRef[],
  runBoards: GroupOptions['runBoards']
): BettingRound[] => {
  const boardsByRun = new Map<number, CardRef[]>();
  (runBoards ?? []).forEach((board, index) => boardsByRun.set(index + 1, dealtCards(board)));
  rounds.forEach((round) => {
    const cards = round.boards[0]?.cards ?? [];
    if (round.run !== undefined && cards.length > 0) boardsByRun.set(round.run, cards);
  });
  if (boardsByRun.size < 2) return rounds;

  const runs = Array.from(boardsByRun.keys()).sort((a, b) => a - b);
  const shared = sharedPrefixLength(runs.map((run) => boardsByRun.get(run) as CardRef[]));
  const isRunStreet = (round: BettingRound) =>
    round.run === undefined &&
    round.key !== 'preflop' &&
    (round.boards[0]?.cards.length || STREET_CARDS[round.key] || 0) > shared;

  const runOne = rounds.filter(isRunStreet).flatMap((round) => round.events);
  const runRounds = runs.map((run) => {
    const board = boardsByRun.get(run) as CardRef[];
    const existing = rounds.find((round) => round.run === run);
    const round: BettingRound = existing ?? {
      key: 'run',
      label: `Run ${run}`,
      run,
      holeCards: hole,
      boards: [{ cards: board }],
      events: []
    };
    return {
      ...round,
      events:
        run === runs[0]
          ? [...runOne, ...round.events].sort((a, b) => a.order - b.order)
          : round.events
    };
  });

  return [
    ...rounds.filter((round) => round.run === undefined && !isRunStreet(round)),
    ...runRounds
  ];
};

export const groupByBettingRound = (
  events: HandHistoryEvent[],
  holeCards?: ReadonlyArray<CardRef | null | undefined>,
  options: GroupOptions = {}
): BettingRound[] => {
  const hole = dealtCards(holeCards);
  const rounds: BettingRound[] = [
    { key: 'preflop', label: LABELS.preflop, holeCards: hole, boards: [], events: [] }
  ];

  [...events]
    .sort((a, b) => a.order - b.order)
    .forEach((event) => {
      const current = rounds[rounds.length - 1];
      if (!isStreetEvent(event)) {
        current.events.push(event);
        return;
      }
      const run = runNumber(event.text);
      const board: RoundBoard = { cards: boardCards(event) };
      // Each runout is its own round, "Run n", its board in the header and its
      // payouts beneath — whether the boards were dealt back to back (the
      // fixtures) or each with its payout rows after it (the live log).
      if (run) {
        rounds.push({
          key: 'run',
          label: `Run ${run}`,
          run: Number(run),
          holeCards: hole,
          boards: [board],
          street: event,
          events: []
        });
        return;
      }
      const key = streetKey(event, current.key);
      rounds.push({
        key,
        label: LABELS[key],
        holeCards: hole,
        boards: [board],
        street: event,
        events: []
      });
    });

  return layOutRuns(
    rounds.filter((round) => round.events.length > 0 || round.street),
    hole,
    options.runBoards
  );
};
